import { Button } from '@artic/ui/button';
import { DatePicker } from '@artic/ui/date-picker';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@artic/ui/dialog';
import { Input } from '@artic/ui/input';
import { Label } from '@artic/ui/label';
import { toast } from '@artic/ui/toast';
import { useEffect, useRef, useState } from 'react';

import { formatIsoDate, parseIsoDate } from '../../components/iso-date';
import {
  ProjectNotEditableError,
  useUpdateProjectPlanning,
  type UpdateProjectPlanningParams,
} from './queries';
import type { ProjectPlanningDetail } from './types';

/**
 * The subset of a project the dialog actually reads — a `Pick` so both the
 * full `ProjectPlanningDetail` (project page) and the narrower
 * `ProjectPlanningListItem` (Project Index rows, ENG-819) satisfy the prop
 * without a detail fetch.
 */
export type EditableProjectFields = Pick<
  ProjectPlanningDetail,
  | 'project_id'
  | 'project_name'
  | 'project_code'
  | 'project_prefix'
  | 'project_scheduled_start_date'
  | 'project_scheduled_complete_date'
>;

interface EditProjectDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  project: EditableProjectFields;
}

type FormState = {
  project_name: string;
  project_code: string;
  project_prefix: string;
  project_scheduled_start_date: string;
  project_scheduled_complete_date: string;
};

function toFormState(project: EditableProjectFields): FormState {
  return {
    project_name: project.project_name,
    project_code: project.project_code,
    project_prefix: project.project_prefix ?? '',
    project_scheduled_start_date: project.project_scheduled_start_date ?? '',
    project_scheduled_complete_date: project.project_scheduled_complete_date ?? '',
  };
}

function validate(form: FormState): string | null {
  if (form.project_name.trim().length === 0) {
    return 'Project name is required';
  }
  if (form.project_code.trim().length === 0) {
    return 'Project code is required';
  }
  if (form.project_prefix && !/^[A-Z0-9]{2,8}$/.test(form.project_prefix)) {
    return 'Project prefix must be 2-8 uppercase alphanumeric characters';
  }
  // ENG-819: both dates are required and complete must be strictly after
  // start — the project window is what Draft Dates spreads into, so a
  // missing or zero-length window has nothing to draft against.
  if (!form.project_scheduled_start_date) {
    return 'Project start date is required';
  }
  if (!form.project_scheduled_complete_date) {
    return 'Project complete date is required';
  }
  if (form.project_scheduled_complete_date <= form.project_scheduled_start_date) {
    return 'Project complete date must be after the start date';
  }
  return null;
}

function buildPatchPayload(form: FormState, baseline: FormState): UpdateProjectPlanningParams {
  const payload: UpdateProjectPlanningParams = {};
  if (form.project_name !== baseline.project_name) payload.project_name = form.project_name;
  if (form.project_code !== baseline.project_code) payload.project_code = form.project_code;
  if (form.project_prefix !== baseline.project_prefix) {
    payload.project_prefix = form.project_prefix || null;
  }
  // validate() has already rejected empty dates — this dialog never clears
  // them, so the values go through as-is.
  if (form.project_scheduled_start_date !== baseline.project_scheduled_start_date) {
    payload.project_scheduled_start_date = form.project_scheduled_start_date;
  }
  if (form.project_scheduled_complete_date !== baseline.project_scheduled_complete_date) {
    payload.project_scheduled_complete_date = form.project_scheduled_complete_date;
  }
  return payload;
}

export function EditProjectDialog({ open, onOpenChange, project }: EditProjectDialogProps) {
  const updateMutation = useUpdateProjectPlanning(project.project_id);

  // Baseline is captured once when the dialog opens and held stable for the
  // session so a background refetch of `project` while the user is editing
  // doesn't shift the diff out from under them. Reset on the next open.
  const baselineRef = useRef<FormState>(toFormState(project));
  const [form, setForm] = useState<FormState>(() => toFormState(project));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const snapshot = toFormState(project);
      baselineRef.current = snapshot;
      setForm(snapshot);
      setError(null);
    }
    // Intentionally omitting `project` from deps — we only want to reset on
    // open, not when a background refetch updates the prop mid-edit. The
    // open-side branch reads `project` via closure, which is fine because
    // it executes synchronously inside the same render.
  }, [open]);

  const isSubmitting = updateMutation.isPending;

  async function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const message = validate(form);
    if (message) {
      setError(message);
      return;
    }

    const payload = buildPatchPayload(form, baselineRef.current);
    if (Object.keys(payload).length === 0) {
      onOpenChange(false);
      return;
    }

    try {
      await updateMutation.mutateAsync(payload);
      // ENG-819: saving never touches work-order dates — the toast says so
      // and points at the redraft flow that reloads them.
      toast.success("Project updated. Work order dates haven't changed — redraft to reload them.");
      onOpenChange(false);
    } catch (err: unknown) {
      if (err instanceof ProjectNotEditableError) {
        setError(err.message);
        return;
      }
      setError(err instanceof Error ? err.message : 'Update failed');
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit project</DialogTitle>
          <DialogDescription>
            Update project metadata. Only draft or redrafting projects can be edited.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="project-name">Name</Label>
            <Input
              id="project-name"
              value={form.project_name}
              onChange={(e) => setForm({ ...form, project_name: e.target.value })}
              autoFocus
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="project-code">Code</Label>
              <Input
                id="project-code"
                value={form.project_code}
                onChange={(e) => setForm({ ...form, project_code: e.target.value })}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="project-prefix">Prefix</Label>
              <Input
                id="project-prefix"
                placeholder="2–8 uppercase alphanumeric"
                value={form.project_prefix}
                onChange={(e) => setForm({ ...form, project_prefix: e.target.value.toUpperCase() })}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="project-start">Start date</Label>
              <DatePicker
                id="project-start"
                aria-label="Start date"
                value={parseIsoDate(form.project_scheduled_start_date)}
                onValueChange={(date) =>
                  setForm({ ...form, project_scheduled_start_date: formatIsoDate(date) })
                }
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="project-end">End date</Label>
              <DatePicker
                id="project-end"
                aria-label="End date"
                value={parseIsoDate(form.project_scheduled_complete_date)}
                onValueChange={(date) =>
                  setForm({ ...form, project_scheduled_complete_date: formatIsoDate(date) })
                }
              />
            </div>
          </div>

          {error && (
            <div role="alert" className="text-destructive text-sm">
              {error}
            </div>
          )}

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Saving…' : 'Save'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
